'use client'
import React from 'react'
import dynamic from 'next/dynamic'
import { cn } from '@/utils/twMerge'
import { useTranslation } from '@/hooks/useTranslation'

const SocialProofStats = dynamic(() => import('./SocialProofStats'))
const SocialProofReview = dynamic(() => import('./SocialProofReview'))
const SocialProofCustomers = dynamic(() => import('./SocialProofCustomers'))
const SocialProofInThePress = dynamic(() => import('./SocialProofInThePress'))

type DataObject = Record<string, any>
type ItemObject = Record<string, any>

const localeData = {
  'en-us': {
    cardTypes: {
      Stats: 'lg:col-span-4',
      Customers: 'lg:col-span-4',
      Review: 'lg:col-span-2',
      'In the press': 'lg:col-span-2',
    },
    components: {
      Stats: (item: ItemObject, data: DataObject, uid?: string) => (
        <SocialProofStats item={item} stats={data.primary.stats_info} uid={uid} />
      ),
      Review: (item: ItemObject, _data: DataObject, uid?: string) => <SocialProofReview item={item} uid={uid} />,
      'In the press': (item: ItemObject, _data: DataObject) => <SocialProofInThePress item={item} />,
      Customers: (item: ItemObject, _data: DataObject) => <SocialProofCustomers item={item} />,
    },
  },
  'de-de': {
    cardTypes: {
      Statistiken: 'lg:col-span-4',
      Kunden: 'lg:col-span-4',
      Rezension: 'lg:col-span-2',
      'In der Presse': 'lg:col-span-2',
    },
    components: {
      Statistiken: (item: ItemObject, data: DataObject, uid?: string) => (
        <SocialProofStats item={item} stats={data.primary.stats_info} uid={uid} />
      ),
      Rezension: (item: ItemObject, _data: DataObject, uid?: string) => <SocialProofReview item={item} uid={uid} />,
      'In der Presse': (item: ItemObject, _data: DataObject) => <SocialProofInThePress item={item} />,
      Kunden: (item: ItemObject, _data: DataObject) => <SocialProofCustomers item={item} />,
    },
  },
  'es-es': {
    cardTypes: {
      Estadísticas: 'lg:col-span-4',
      Clientes: 'lg:col-span-4',
      Revisar: 'lg:col-span-2',
      'En la prensa': 'lg:col-span-2',
    },
    components: {
      Estadísticas: (item: ItemObject, data: DataObject, uid?: string) => (
        <SocialProofStats item={item} stats={data.primary.stats_info} uid={uid} />
      ),
      Revisar: (item: ItemObject, _data: DataObject, uid?: string) => <SocialProofReview item={item} uid={uid} />,
      'En la prensa': (item: ItemObject, _data: DataObject) => <SocialProofInThePress item={item} />,
      Clientes: (item: ItemObject, _data: DataObject) => <SocialProofCustomers item={item} />,
    },
  },
  'fr-fr': {
    cardTypes: {
      Statistiques: 'lg:col-span-4',
      Clients: 'lg:col-span-4',
      Revoir: 'lg:col-span-2',
      'Dans la presse': 'lg:col-span-2',
    },
    components: {
      Statistiques: (item: ItemObject, data: DataObject, uid?: string) => (
        <SocialProofStats item={item} stats={data.primary.stats_info} uid={uid} />
      ),
      Revoir: (item: ItemObject, _data: DataObject, uid?: string) => <SocialProofReview item={item} uid={uid} />,
      'Dans la presse': (item: ItemObject, _data: DataObject) => <SocialProofInThePress item={item} />,
      Clients: (item: ItemObject, _data: DataObject) => <SocialProofCustomers item={item} />,
    },
  },
}

import { useLocale } from '@/context/LocaleContext'

const SocialProofComponent = ({ data, uid }: { data: DataObject; uid?: string }) => {
  const locale = useLocale()
  const { t } = useTranslation(locale)

  const getGridColSpan = (cardType: string): string => {
    const localeCardTypes = (localeData[locale as keyof typeof localeData] as { cardTypes: Record<string, string> })
      .cardTypes
    return localeCardTypes[t(cardType as 'stats' | 'customers')] || 'lg:col-span-2'
  }

  const getSocialProofCardData = (item: ItemObject, uid?: string) => {
    const localeComponents = (
      localeData[locale as keyof typeof localeData] as {
        components: Record<string, (item: ItemObject, data: DataObject, uid?: string) => React.JSX.Element>
      }
    ).components
    return localeComponents[t(item.type)](item, data, uid)
  }

  return (
    <section
      className={cn(
        'relative pb-24',
        uid === 'request-cmmc-demo' && 'bg-primary-ink-100',
        ['nist', 'cmmc', 'request-cmmc-demo'].includes(uid as string) && 'pb-10 pt-20 lg:pt-[140px]',
      )}>
      <div className={['nist', 'cmmc', 'request-cmmc-demo'].includes(uid as string) ? 'container' : 'container-fluid'}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-6">
          {data.primary.social_proof.map((item: ItemObject) => {
            return (
              <div
                key={item.type}
                className={cn(
                  'relative min-h-[500px] overflow-hidden rounded-md border px-8 py-6 backdrop-blur-25',
                  uid === 'request-cmmc-demo'
                    ? 'border-white/10 bg-[#223039] shadow-dark'
                    : 'border-primary-ink-6 shadow-light',
                  getGridColSpan(item.type),
                )}>
                {getSocialProofCardData(item, uid)}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default SocialProofComponent
